import { useForm } from '@tanstack/react-form'
import { useEffect, useState } from 'react'
import { ExpenseItemInputSchema } from '../schema/expensesSchema'
import type { ExpenseItemData, ExpenseItemInput } from '../schema/expensesSchema'
import type { ExpenseItem, ExpensesCategory } from '../types/expenses'
import { AutoCompleteSelect } from '@/components/custom/auto-complete-select'
import CurrencyInput from '@/components/custom/currency-input'
import { FormInput } from '@/components/custom/form-input'
import { DragDropInput } from '@/components/custom/drag-drop-input'
import { Button } from '@/components/ui/button'
import { Modal } from '@/components/custom/modal'

const defaultValues: Partial<ExpenseItemInput> = {
  amount: 0,
  itemDescription: '',
  categoryId: '',
}

interface ExpenseItemFormProps {
  expenseItem?: ExpenseItem | ExpenseItemInput
  categories: Array<ExpensesCategory>
  open: boolean
  onClose: () => void
  onSubmit?: (item: ExpenseItemInput | ExpenseItemData) => void
}

export function ExpenseItemForm({
  expenseItem,
  categories,
  open,
  onClose,
  onSubmit,
}: ExpenseItemFormProps) {
  const [invoiceFile, setInvoiceFile] = useState<File | null>(null)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [existingReceiptUrl, setExistingReceiptUrl] = useState<string | null>(null)

  // Set up initial file and preview when expenseItem changes
  useEffect(() => {
    if (expenseItem && 'file' in expenseItem && expenseItem.file instanceof File) {
      setInvoiceFile(expenseItem.file)
      setPreviewUrl(URL.createObjectURL(expenseItem.file))
      setExistingReceiptUrl(null)
    } else if (expenseItem && 'receiptUrl' in expenseItem && expenseItem.receiptUrl) {
      setInvoiceFile(null)
      setPreviewUrl(null)
      setExistingReceiptUrl(expenseItem.receiptUrl)
    } else {
      setInvoiceFile(null)
      setPreviewUrl(null)
      setExistingReceiptUrl(null)
    }

    // Cleanup function to revoke object URL
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl)
      }
    }
  }, [expenseItem])

  const initialValue = expenseItem
    ? {
        itemDescription: expenseItem.itemDescription,
        categoryId: expenseItem.categoryId,
        amount: expenseItem.amount,
      }
    : defaultValues

  const form = useForm({
    defaultValues: initialValue,
    onSubmit: ({ value }) => {
      let submittedItem: ExpenseItemInput | ExpenseItemData

      if (invoiceFile) {
        submittedItem = { ...value, file: invoiceFile } as ExpenseItemInput
      } else if (expenseItem && '_id' in expenseItem && expenseItem._id) {
        submittedItem = {
          _id: expenseItem._id,
          categoryId: value.categoryId,
          itemDescription: value.itemDescription,
          amount: value.amount,
          receiptUrl: existingReceiptUrl ?? '',
        } as ExpenseItemData
      } else {
        return
      }

      form.reset()
      setInvoiceFile(null)
      setPreviewUrl(null)
      setExistingReceiptUrl(null)
      onSubmit?.(submittedItem)
      onClose()
    },
  })

  const handleFileSelect = (file: File | null) => {
    if (file) {
      setInvoiceFile(file)
      setPreviewUrl(URL.createObjectURL(file))
    } else {
      setInvoiceFile(null)
      setPreviewUrl(null)
    }
  }

  const handleClose = () => {
    form.reset()
    setInvoiceFile(null)
    setPreviewUrl(null)
    setExistingReceiptUrl(null)
    onClose()
  }

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={expenseItem ? 'Edit Expense Item' : 'Add Expense Item'}
      direction="right"
    >
      <div>
        <form
          id="donation-form"
          onSubmit={(e) => {
            e.preventDefault()
            e.stopPropagation()
            form.handleSubmit()
          }}
          className="space-y-2"
        >
          {/* Watch donorId changes and auto-update churchId */}
          <form.Field
            name="itemDescription"
            validators={{
              onBlur: ({ value }) => {
                const result =
                  ExpenseItemInputSchema.shape.itemDescription.safeParse(value)
                return result.success
                  ? undefined
                  : result.error.errors[0].message
              },
            }}
            children={(field) => {
              return (
                <div className="gap-0">
                  <FormInput
                    field={field}
                    label="Description"
                    id={field.name}
                    name={field.name}
                    required
                  />
                </div>
              )
            }}
          />

          <form.Field
            name="categoryId"
            validators={{
              onBlur: ({ value }) => {
                const result =
                  ExpenseItemInputSchema.shape.categoryId.safeParse(value)
                return result.success
                  ? undefined
                  : result.error.errors[0].message
              },
            }}
            children={(field) => (
              <div className="gap-0">
                <AutoCompleteSelect
                  field={field}
                  label="Purpose / Category"
                  id={field.name}
                  name={field.name}
                  options={categories.map((category) => ({
                    value: category._id,
                    label: category.name,
                    description: category.description,
                  }))}
                  required
                />
              </div>
            )}
          />

          <div className="grid grid-cols-2 gap-4">
            <form.Field
              name="amount"
              validators={{
                onBlur: ({ value }) => {
                  const result =
                    ExpenseItemInputSchema.shape.amount.safeParse(value)
                  return result.success
                    ? undefined
                    : result.error.errors[0].message
                },
              }}
              children={(field) => {
                return (
                  <div className="gap-0">
                    <CurrencyInput
                      field={field}
                      label="Amount"
                      id={field.name}
                      name={field.name}
                      className="text-right"
                      required
                    />
                  </div>
                )
              }}
            />
          </div>

          <DragDropInput
            onFileSelect={handleFileSelect}
            selectedFile={invoiceFile}
            accept="image/*,application/pdf"
            label="Expense Receipt"
            previewUrl={previewUrl}
            existingFileName={
              existingReceiptUrl
                ? existingReceiptUrl.split('/').pop() ?? null
                : null
            }
            required={!existingReceiptUrl}
          />

          <form.Subscribe
            selector={(state) => ({
              canSubmit: state.canSubmit,
              values: state.values,
            })}
            children={({ canSubmit, values }) => {
              const hasFile = invoiceFile !== null || existingReceiptUrl !== null
              const allFieldsFilled =
                values.itemDescription &&
                values.categoryId &&
                (values.amount ?? 0) > 0 &&
                hasFile

              const isFormValid = canSubmit && allFieldsFilled

              return (
                <Button
                  type="submit"
                  className="w-full mt-4"
                  disabled={!isFormValid}
                >
                  Add Item
                </Button>
              )
            }}
          />
        </form>
      </div>
    </Modal>
  )
}
